import { MailOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input } from "antd";
import { useState } from "react";
import { useRequestSetupAccessMutation } from "../../service/implementation.service";
import styles from "./setup.module.scss";

type Props = {
  token: string;
  groupName: string;
  /** Set when a magic link was present but rejected, so we explain before asking again. */
  linkError?: string;
};

/**
 * Email confirmation gate.
 *
 * The portal URL is stable and shareable, but it is not the access control — this
 * page collects live EHR credentials, so we confirm the visitor's address against
 * the group's allowlist and email them a single-use link (issue §6).
 */
export default function AccessGate({ token, groupName, linkError }: Props) {
  const [requestAccess, { isLoading }] = useRequestSetupAccessMutation();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async ({ email }: { email: string }) => {
    setError(null);
    try {
      await requestAccess({ token, email }).unwrap();
      setSentTo(email);
    } catch {
      setError("Something went wrong sending that link. Please try again in a moment.");
    }
  };

  if (sentTo) {
    return (
      <div className={styles.gate}>
        <MailOutlined className={styles.gateIcon} />
        <h1 className={styles.gateTitle}>Check your email</h1>
        <p className={styles.gateBody}>
          If <strong>{sentTo}</strong> is on this account, we've just sent a link to open your onboarding portal. It
          expires in 30 minutes.
        </p>
        <Button type="link" onClick={() => setSentTo(null)}>
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.gate}>
      <h1 className={styles.gateTitle}>Nudge onboarding</h1>
      <p className={styles.gateBody}>
        {groupName ? (
          <>
            Onboarding for <strong>{groupName}</strong>.{" "}
          </>
        ) : null}
        Enter your email and we'll send you a link to open it.
      </p>

      {linkError ? (
        <Alert type="warning" showIcon message={linkError} style={{ marginBottom: 20, textAlign: "left" }} />
      ) : null}
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 20, textAlign: "left" }} /> : null}

      <Form layout="vertical" onFinish={onSubmit} requiredMark={false}>
        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Please enter your email address." },
            { type: "email", message: "That doesn't look like a valid email address." },
          ]}
        >
          <Input size="large" placeholder="you@yourpractice.com" autoComplete="email" />
        </Form.Item>
        <Button type="primary" size="large" htmlType="submit" loading={isLoading} block>
          Email me a link
        </Button>
      </Form>

      <span className={styles.gateFootnote}>
        We confirm your email each time because this page holds your EHR login details.
      </span>
    </div>
  );
}
